import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './AuthContext';
import ForcePasswordChange from './components/ForcePasswordChange';
import '@/App.css';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import PublicBadge from './pages/PublicBadge';
import StudentDashboard from './pages/StudentDashboard';
import TeacherDashboard from './pages/TeacherDashboard';
import TeacherCalendarPage from './pages/TeacherCalendarPage';
import StudentCoursePage from './pages/StudentCoursePage';
import TeacherCoursePage from './pages/TeacherCoursePage';
import PaymentPage from './pages/PaymentPage';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCancel from './pages/PaymentCancel';
import PaymentHistory from './pages/PaymentHistory';
import SubscriptionManagement from './pages/SubscriptionManagement';
import ClassBooking from './pages/ClassBooking';
import AdminDashboard from './pages/AdminDashboard';
import ParentDashboard from './pages/ParentDashboard';
import DiscussionBoard from './pages/DiscussionBoard';
import TeacherProfilePage from './pages/TeacherProfilePage';
import ProgramsPage from './pages/ProgramsPage';
import ImpersonateBridge from './pages/ImpersonateBridge';

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading, requiresPasswordChange } = useAuth();

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return (
    <>
      {requiresPasswordChange && <ForcePasswordChange />}
      {children}
    </>
  );
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/teacher-profile/:teacherId" element={<TeacherProfilePage />} />
      <Route path="/programs" element={<ProgramsPage />} />
      <Route path="/impersonate" element={<ImpersonateBridge />} />
      <Route path="/badge/:shareId" element={<PublicBadge />} />
      
      {/* Student Routes */}
      <Route
        path="/student/dashboard"
        element={
          <ProtectedRoute allowedRoles={['student']}>
            <StudentDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/student/course/:courseId"
        element={
          <ProtectedRoute allowedRoles={['student']}>
            <StudentCoursePage />
          </ProtectedRoute>
        }
      />
      
      {/* Teacher Routes */}
      <Route
        path="/teacher/dashboard"
        element={
          <ProtectedRoute allowedRoles={['teacher']}>
            <TeacherDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/teacher/course/:courseId"
        element={
          <ProtectedRoute allowedRoles={['teacher']}>
            <TeacherCoursePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/teacher/calendar"
        element={
          <ProtectedRoute allowedRoles={['teacher']}>
            <TeacherCalendarPage />
          </ProtectedRoute>
        }
      />
      
      {/* Admin Routes */}
      <Route
        path="/admin/dashboard"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />

      {/* Parent Routes */}
      <Route
        path="/parent/dashboard"
        element={
          <ProtectedRoute allowedRoles={['parent']}>
            <ParentDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/parent/hub"
        element={
          <ProtectedRoute allowedRoles={['parent']}>
            <ParentDashboard />
          </ProtectedRoute>
        }
      />

      {/* Discussion Board - accessible by both teachers and students */}
      <Route
        path="/discussions"
        element={
          <ProtectedRoute allowedRoles={['student', 'teacher']}>
            <DiscussionBoard />
          </ProtectedRoute>
        }
      />
      
      {/* Payment Routes */}
      <Route
        path="/payment"
        element={
          <ProtectedRoute allowedRoles={['student']}>
            <PaymentPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/payment/success"
        element={
          <ProtectedRoute allowedRoles={['student']}>
            <PaymentSuccess />
          </ProtectedRoute>
        }
      />
      <Route
        path="/payment/cancel"
        element={
          <ProtectedRoute allowedRoles={['student']}>
            <PaymentCancel />
          </ProtectedRoute>
        }
      />
      <Route
        path="/payment/history"
        element={
          <ProtectedRoute allowedRoles={['student']}>
            <PaymentHistory />
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscriptions"
        element={
          <ProtectedRoute allowedRoles={['student']}>
            <SubscriptionManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/bookings"
        element={
          <ProtectedRoute allowedRoles={['student']}>
            <ClassBooking />
          </ProtectedRoute>
        }
      />
      <Route
        path="/class-booking"
        element={
          <ProtectedRoute allowedRoles={['student']}>
            <ClassBooking />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="App relative">
          {/* Animated Stars Background */}
          <div className="stars-container" aria-hidden="true">
            <div className="stars-layer stars-sm" />
            <div className="stars-layer stars-md" />
            <div className="stars-layer stars-lg" />
          </div>
          <div className="relative z-10">
            <AppRoutes />
            <Toaster position="top-right" richColors />
          </div>
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
